import React, { useState, useEffect, useCallback } from "react"
import "../styles/popUp.css"
import { XMarkIcon } from "@heroicons/react/24/outline"
import STRINGS from "../globals/strings"
import endpoints from "../api/endpoints"

function PopUp({ bowler, onClose, matchId, matchType }) {
	const [oversList, setOversList] = useState([])
	const [matchSettings, setMatchSettings] = useState(null)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)

	const fetchBowlerHistory = useCallback(async () => {
		if (!matchId || !matchType || !bowler?.bowler_name) {
			setError(STRINGS.MISSING_PARAMETERS)
			setOversList([])
			setMatchSettings(null)
			return
		}

		try {
			setLoading(true)
			setError(null)

			console.log(`Sending BowlerHistory API request:`, { matchId, matchType, bowler })

			const result = await endpoints.fetchBowlerHistory(matchId, matchType, bowler)

			if (result.success && result.data && Array.isArray(result.data.over_data) && result.data.over_data.length > 0) {
				setMatchSettings(result.data.match_settings || null)

				const mappedOvers = result.data.over_data.map(over => ({
					over_number: over.over_number || "0.0",
					total_run: over.total_run || 0,
					balls: (over.over_info || []).map(ball => ({
						runs: ball.runs !== undefined ? ball.runs : 0,
						is_four: !!ball.is_four,
						is_six: !!ball.is_six,
						is_out: !!ball.is_out,
						is_wide_ball: !!ball.is_wide_ball,
						is_no_ball: !!ball.is_no_ball,
						is_bye: !!ball.is_bye,
						is_leg_bye: !!ball.is_leg_bye
					}))
				}))
				setOversList(mappedOvers)
			} else {
				setMatchSettings(null)
				setError(result.message || STRINGS.NO_OVER_DATA)
				setOversList([])
			}
		} catch (err) {
			setError(`${STRINGS.FAILED_TO_FETCH_BOWLER_HISTORY}${err.message}`)
			setOversList([])
			setMatchSettings(null)
		} finally {
			setLoading(false)
		}
	}, [bowler, matchId, matchType])

	useEffect(() => {
		fetchBowlerHistory()
		document.body.classList.add("modal-open")
		return () => {
			document.body.classList.remove("modal-open")
		}
	}, [fetchBowlerHistory])

	const getDotColor = (ball) => {
		if (ball.is_out) return "dot red"
		if (ball.is_six) return "dot green"
		if (ball.is_four) return "dot green"
		if (ball.is_wide_ball) return "dot gray"
		if (ball.is_no_ball) return "dot gray"
		if (ball.is_bye || ball.is_leg_bye) return "dot gray"
		return "dot gray"
	}

	// Returns the run value to display based on match_settings.
	// If a ball type has allowed = 1, subtract 1 from runs (the extra run is a penalty, not a scored run).
	// If allowed = 0, show runs as-is.
	const getDisplayRuns = (ball) => {
		if (!matchSettings) return ball.runs

		if (ball.is_wide_ball && matchSettings.is_wide_ball_allowed === 1) return ball.runs - 1
		if (ball.is_no_ball && matchSettings.is_no_ball_allowed === 1) return ball.runs - 1

		return ball.runs
	}

	return (
		<div className="pop-up">
			<div className="pop-up-header">
				<label className="bowling-label">
					{STRINGS.BOWLING_HIGHLIGHTS}
					<XMarkIcon
						className="cancel-icon"
						onClick={onClose}
						style={{ height: "30px", width: "30px" }}
					/>
				</label>
				<label className="name-label">
					{bowler?.bowler_name || STRINGS.UNKNOWN_BOWLER}
				</label>
			</div>
			<div className="scrollable-popup-contant">
				{loading ? (
					<div className="loader">{STRINGS.LOADING_BOWLER_HIGHLIGHTS}</div>
				) : error ? (
					<div className="error-message">
						<p>{error}</p>
					</div>
				) : oversList.length > 0 ? (
					oversList.map((over, index) => (
						<React.Fragment key={index}>
							<div className="over-row">
								<div className="over-dots">
									<div style={{ display: "flex", justifyContent: "space-between" }}>
										<label className="over-label">{STRINGS.OVER_LABEL(over.over_number)}</label>
										<label className="runs-label">{STRINGS.RUNS_LABEL(over.total_run)}</label>
									</div>
									<div className="dots-container">
										{over.balls.map((ball, idx) => {
											const isSpecial = ball.is_wide_ball || ball.is_no_ball || ball.is_bye || ball.is_leg_bye

											const getLabel = () => {
												const displayRuns = getDisplayRuns(ball)
												const parts = []

												const fmt = (runs, type) => runs > 0 ? `${runs}${type}` : type

												if (ball.is_wide_ball) parts.push(fmt(ball.is_out ? displayRuns : 0, "WD"))
												else if (ball.is_no_ball) parts.push(fmt(ball.is_out ? displayRuns : 0, "NB"))
												else if (ball.is_bye) parts.push(fmt(ball.is_out ? displayRuns : 0, "BYE"))
												else if (ball.is_leg_bye) parts.push(fmt(ball.is_out ? displayRuns : 0, "LB"))

												if (ball.is_out && parts.length === 0) {
													parts.push(fmt(displayRuns, "WK"))
												}

												return parts
											}

											const labels = getLabel()

											return (
												<div key={idx} style={{
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
													// minWidth: "40px" 
												}}>
													<span className={isSpecial && ball.is_out ? "dot red" : getDotColor(ball)}>
														{isSpecial && ball.is_out ? "W" : getDisplayRuns(ball)}
													</span>
													{labels.length > 0 && (
														<div style={{
															display: "flex",
															flexDirection: "column",
															alignItems: "center",
															marginTop: "5px",
															gap: "1px"
														}}>
															{labels.map((label, i) => (
																<span key={i} style={{
																	fontWeight: "500",
																	fontSize: "12px",
																	lineHeight: "14px",
																	textAlign: "center",
																	color: "#717171",
																	whiteSpace: "nowrap"  // 👈 prevents label text from wrapping
																}}>
																	{label}
																</span>
															))}
														</div>
													)}
												</div>
											)
										})}
									</div>
								</div>
							</div>
						</React.Fragment>
					))
				) : (
					<div className="no-data">
						<p>{STRINGS.NO_OVER_HISTORY}</p>
					</div>
				)}
			</div>
		</div>
	)
}

export default PopUp